import { useEntityReferenceDirectories } from "@/hooks/use-entity-reference-directories.hook.ts";
import { InformBox } from "@/components/inform-box/InformBox.tsx";
import { DeleteListItem } from "@/components/list-item/DeleteListItem.tsx";
import {
  ConfirmationModal,
  useModals,
  useToasts,
  Modal,
  ModalProps,
  Button,
  ButtonLink,
  FontAwesomeIcon,
  Input,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
} from "@skailer-com/uikit";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { CustomFieldOptions } from "@/pages/settings/custom-fields/components/CustomFieldOptions.tsx";
import { CustomFieldChoiceTypeStep } from "@/pages/settings/custom-fields/components/CustomFieldChoiceTypeStep.tsx";
import { useTableFieldDelete, useTableFieldSave } from "@/queries/use-custom-fields.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiField,
  ApiFieldTypes,
  ApiFieldValueTypes,
  ApiFieldReferenceEntity,
  ApiTable,
  ApiTableTypes,
  CustomFieldOptionPayload,
  LINKED_FIELD_BY_REFERENCE_ENTITY,
} from "@/types/custom-field.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

enum EmployeeCustomFieldCreateSteps {
  choiceType = "choiceType",
  addData = "addData",
}

enum FieldVisibility {
  never = "never",
  whenFilled = "when_filled",
  always = "always",
}

interface EmployeeCustomTableFieldEditModalProps extends ModalProps {
  field?: ApiField;
  table: ApiTable;
}

const LINK_LABEL_KEYS: Record<ApiFieldReferenceEntity, { title: string; hint: string }> = {
  [ApiFieldReferenceEntity.legal_entity]: {
    title: "custom_fields:link_to_employment_legal_entity",
    hint: "custom_fields:link_to_employment_legal_entity_hint",
  },
  [ApiFieldReferenceEntity.department]: {
    title: "custom_fields:link_to_position_department",
    hint: "custom_fields:link_to_position_department_hint",
  },
  [ApiFieldReferenceEntity.division]: {
    title: "custom_fields:link_to_position_division",
    hint: "custom_fields:link_to_position_division_hint",
  },
  [ApiFieldReferenceEntity.location]: {
    title: "custom_fields:link_to_position_location",
    hint: "custom_fields:link_to_position_location_hint",
  },
};

export function EmployeeCustomTableFieldEditModal({
  field,
  table,
  ...props
}: EmployeeCustomTableFieldEditModalProps) {
  const { id } = props;
  const isEdit = !!field;
  const isCustomTable = table.table_type === ApiTableTypes.custom;
  const isLinkTaken = (entity: ApiFieldReferenceEntity) =>
    table.table_fields.some(
      (tableField) =>
        tableField.linked_field === LINKED_FIELD_BY_REFERENCE_ENTITY[entity] &&
        tableField.id !== field?.id
    );
  const isSystemField = Boolean(
    field?.is_system ||
    field?.type === ApiFieldTypes.effective_from ||
    field?.type === ApiFieldTypes.effective_to ||
    field?.type === ApiFieldTypes.changed_by
  );
  const isMandatoryReadOnly = Boolean(
    field?.is_system_mandatory ||
    field?.type === ApiFieldTypes.changed_by ||
    field?.type === ApiFieldTypes.effective_to
  );
  const { close, openModal } = useModals();
  const { t } = useTranslation();
  const referenceDirectories = useEntityReferenceDirectories();
  const { showInfo } = useToasts();
  const { getFieldTypeInformation, fieldTypeInfo, getFieldTitle } = useCustomFieldsHook();
  const localizedSystemFieldTitle =
    isSystemField && field ? getFieldTitle({ type: field.type, title: "" }).trim() : "";
  const [step, setStep] = useState<EmployeeCustomFieldCreateSteps>(
    isEdit ? EmployeeCustomFieldCreateSteps.addData : EmployeeCustomFieldCreateSteps.choiceType
  );
  const [currentOptions, setCurrentOptions] = useState<CustomFieldOptionPayload[]>(
    ...[field?.options ?? []]
  );
  const currentOptionsRef = useRef<CustomFieldOptionPayload[]>(currentOptions);
  const [optionsError, setOptionsError] = useState<string>("");
  const [chosenType, setChosenType] = useState<ApiFieldValueTypes | undefined>(field?.field_type);
  const TableFieldSchemaObject = z.object({
    title: z
      .string({ error: t("errors:field_required") })
      .trim()
      .min(1, t("errors:field_required")),
    description: z.string().optional(),
    hint: z.string().optional(),
    is_mandatory: z.boolean().optional(),
    visibility: z.enum(FieldVisibility),
    is_multiple: z.boolean().optional(),
    reference_entity: z.enum(ApiFieldReferenceEntity).optional(),
    is_linked: z.boolean(),
  });

  const { canIEdit } = usePermissions();

  const {
    handleSubmit,
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(TableFieldSchemaObject),
    defaultValues: {
      title: field?.title?.trim() || localizedSystemFieldTitle,
      description: field?.description ?? "",
      is_mandatory: field?.is_mandatory ?? false,
      visibility: field?.is_hidden
        ? FieldVisibility.never
        : isSystemField
          ? FieldVisibility.whenFilled
          : FieldVisibility.always,
      is_multiple: field?.linked_field ? false : (field?.config?.is_multiple_values ?? false),
      reference_entity: field?.reference_entity ?? ApiFieldReferenceEntity.legal_entity,
      is_linked: Boolean(field?.linked_field),
      hint: field?.hint ?? "",
    },
  });
  const fieldTitle = watch("title");
  const isLocalizedSystemFieldTitle =
    isSystemField && fieldTitle?.trim() === localizedSystemFieldTitle;
  const showSystemFieldTitleReset =
    isSystemField && Boolean(localizedSystemFieldTitle) && !isLocalizedSystemFieldTitle;
  const visibility = watch("visibility");
  const referenceEntity = watch("reference_entity");
  const isLinked = watch("is_linked");
  const canLink = (
    entity: ApiFieldReferenceEntity | undefined
  ): entity is ApiFieldReferenceEntity =>
    isCustomTable &&
    !isSystemField &&
    chosenType === ApiFieldValueTypes.entity_reference &&
    !!entity &&
    !isLinkTaken(entity);
  const isMultipleDisabled = canLink(referenceEntity) && isLinked;
  const { mutateAsync: save, isPending: isSavePending } = useTableFieldSave(control);
  const { mutateAsync: fieldDelete } = useTableFieldDelete();

  const chosenTypeItem = useMemo(() => {
    return chosenType ? getFieldTypeInformation(chosenType) : undefined;
  }, [chosenType]);

  const title =
    step == EmployeeCustomFieldCreateSteps.choiceType
      ? t("custom_fields:choose_field_type")
      : !field
        ? t("custom_fields:create_new_field_type", {
            field_type: chosenTypeItem?.title.toLowerCase(),
          })
        : t("custom_fields:edit_field_type", {
            field_type: chosenTypeItem?.title.toLowerCase(),
          });

  useEffect(() => {
    currentOptionsRef.current = currentOptions;

    if (currentOptions.length > 0) {
      setOptionsError("");
    }
  }, [currentOptions]);

  const onSubmit = handleSubmit(async (data) => {
    if (!chosenType) return;

    if (
      !isSystemField &&
      chosenType === ApiFieldValueTypes.select &&
      currentOptionsRef.current.length === 0
    ) {
      setOptionsError(t("custom_fields:options_required_for_select"));
      return;
    }

    setOptionsError("");
    const isMandatory =
      data.visibility === FieldVisibility.never ? false : (data.is_mandatory ?? false);
    const systemFieldMandatoryPayload = isMandatoryReadOnly ? {} : { is_mandatory: isMandatory };
    const systemFieldTitlePayload =
      data.title === localizedSystemFieldTitle
        ? field?.title?.trim()
          ? { title: null }
          : {}
        : { title: data.title };

    const fieldSaved = await save(
      isSystemField
        ? {
            tableId: table.id,
            fieldId: field!.id,
            payload: {
              ...systemFieldTitlePayload,
              description: data.description ?? "",
              hint: data.hint ?? "",
              ...systemFieldMandatoryPayload,
              is_hidden: data.visibility === FieldVisibility.never,
            },
          }
        : {
            tableId: table.id,
            fieldId: field?.id,
            payload: {
              field_type: chosenType,
              title: data.title,
              description: data.description ?? "",
              hint: data.hint ?? "",
              is_mandatory: isMandatory,
              is_hidden: data.visibility === FieldVisibility.never,
              config: {
                is_multiple_values:
                  canLink(data.reference_entity) && data.is_linked
                    ? false
                    : (data.is_multiple ?? false),
              },
              reference_entity:
                !isEdit && chosenType === ApiFieldValueTypes.entity_reference
                  ? data.reference_entity
                  : undefined,
              ...(!isEdit && canLink(data.reference_entity) && data.is_linked
                ? { linked_field: LINKED_FIELD_BY_REFERENCE_ENTITY[data.reference_entity] }
                : {}),
              options: currentOptionsRef.current,
            },
          }
    );

    if (fieldSaved) {
      // Без дефолтов пока
      // openModal(EmployeeCustomFieldDefaultValue, { field: fieldSaved, fromEdit: !!field });

      if (field) {
        analyticsService.trackEvent(analyticEvents.customFields.fieldEdited, {
          [analyticProperties.id]: fieldSaved.id,
        });
      } else {
        analyticsService.trackEvent(analyticEvents.customFields.fieldCreated, {
          [analyticProperties.id]: fieldSaved.id,
        });
      }

      close(id);
    }
  });

  const onNext = useCallback(async () => {
    if (step == EmployeeCustomFieldCreateSteps.choiceType) {
      setStep(EmployeeCustomFieldCreateSteps.addData);
    } else {
      await onSubmit();
    }
  }, [step]);

  const onBack = useCallback(() => {
    if (step == EmployeeCustomFieldCreateSteps.choiceType || isEdit) return;

    return () => {
      setStep(EmployeeCustomFieldCreateSteps.choiceType);
    };
  }, [step]);

  const isTypeWithOptions = useMemo(() => {
    if (!chosenType) return false;

    return [ApiFieldValueTypes.select].includes(chosenType);
  }, [chosenType]);

  const handleDelete = () => {
    if (!field) return;

    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:yes"),
      onAccept: async function () {
        try {
          const result = await fieldDelete({ tableId: table.id, fieldId: field.id });

          if (result) showInfo({ title: t("custom_fields:field_deleted") });

          analyticsService.trackEvent(analyticEvents.customFields.fieldDeleted, {
            [analyticProperties.id]: field.id,
          });
        } catch {
          return;
        }

        close(id);
      },
    });
  };

  return (
    <Modal
      {...props}
      title={title}
      onBack={onBack()}
      withCloser={true}
      layout={"stretch"}
      actions={
        <Stack gap={"md"}>
          <ServerErrorField errors={errors} />
          {optionsError && (
            <Stack className={"h-14"} justify={"center"}>
              <Text className={"text-danger"}>{optionsError}</Text>
            </Stack>
          )}

          <Button size={"lg"} variant={"primary"} onClick={onNext} isLoading={isSavePending}>
            {step == EmployeeCustomFieldCreateSteps.addData ? t("common:save") : t("common:next")}
          </Button>
        </Stack>
      }
    >
      {step == EmployeeCustomFieldCreateSteps.choiceType ? (
        <CustomFieldChoiceTypeStep
          fieldTypeInfo={fieldTypeInfo}
          value={chosenType}
          onChange={setChosenType}
        />
      ) : (
        <></>
      )}
      {step == EmployeeCustomFieldCreateSteps.addData ? (
        <form onSubmit={onSubmit}>
          <Stack gap={"md"}>
            <Stack>
              <Controller
                control={control}
                name="title"
                render={({ field: { value, onChange, onBlur, ref } }) => (
                  <Input
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    ref={ref}
                    label={t("common:title")}
                    error={errors.title?.message}
                    required
                  />
                )}
              />
              {showSystemFieldTitleReset && (
                <Stack direction="horizontal" items="center" gap="xs" className="py-2">
                  <Text className="text-sm text-secondary">
                    {t("custom_fields:system_field_title_reset_hint")}
                  </Text>
                  <button
                    type="button"
                    className="cursor-pointer text-sm text-brand-dark underline hover:no-underline"
                    onClick={() => {
                      setValue("title", localizedSystemFieldTitle, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }}
                  >
                    {t("common:reset")}
                  </button>
                </Stack>
              )}
              {chosenType &&
                [
                  ApiFieldValueTypes.text,
                  ApiFieldValueTypes.textarea,
                  ApiFieldValueTypes.phone,
                  ApiFieldValueTypes.number,
                  ApiFieldValueTypes.percentage,
                  ApiFieldValueTypes.email,
                  ApiFieldValueTypes.url,
                  ApiFieldValueTypes.select,
                  ApiFieldValueTypes.text_array,
                  ApiFieldValueTypes.employee_reference,
                  ApiFieldValueTypes.entity_reference,
                ].includes(chosenType) && (
                  <Input
                    label={t("custom_fields:placeholder")}
                    {...register("description")}
                    error={errors.description?.message}
                  />
                )}
              <Textarea
                label={t("custom_fields:hint")}
                {...register("hint")}
                error={errors.hint?.message}
              />

              <Controller
                render={({ field: { onChange, value } }) => (
                  <Select
                    label={t("custom_fields:visibility")}
                    emptyTitle={t("common:select")}
                    value={value}
                    options={[
                      {
                        id: FieldVisibility.never,
                        title: t("custom_fields:visibility_never"),
                      },
                      ...(isSystemField
                        ? [
                            {
                              id: FieldVisibility.whenFilled,
                              title: t("custom_fields:visibility_when_filled"),
                            },
                          ]
                        : [
                            {
                              id: FieldVisibility.always,
                              title: t("custom_fields:visibility_always"),
                            },
                          ]),
                    ]}
                    onChange={(nextVisibility) => {
                      onChange(nextVisibility);
                      if (nextVisibility === FieldVisibility.never) {
                        setValue("is_mandatory", false);
                      }
                    }}
                    disabled={field?.is_system_mandatory && field.type !== ApiFieldTypes.changed_by}
                  />
                )}
                name={"visibility"}
                control={control}
              />
            </Stack>
            <Controller
              render={({ field: { onChange, value } }) => (
                <Switch
                  onChange={onChange}
                  checked={value}
                  disabled={visibility === FieldVisibility.never || isMandatoryReadOnly}
                  title={t("custom_fields:mandatory")}
                  description={t("custom_fields:make_mandatory")}
                />
              )}
              name={"is_mandatory"}
              control={control}
            />
            {!isSystemField &&
              (chosenType === ApiFieldValueTypes.select ||
                chosenType === ApiFieldValueTypes.employee_reference ||
                chosenType === ApiFieldValueTypes.entity_reference) && (
                <Controller
                  render={({ field: { onChange, value } }) => (
                    <Switch
                      onChange={onChange}
                      checked={value}
                      disabled={isMultipleDisabled}
                      title={t("custom_fields:multiple")}
                      description={t(
                        chosenType === ApiFieldValueTypes.employee_reference
                          ? "custom_fields:multiple_employees_description"
                          : chosenType === ApiFieldValueTypes.entity_reference
                            ? "custom_fields:multiple_references_description"
                            : "custom_fields:multiple_description"
                      )}
                    />
                  )}
                  name={"is_multiple"}
                  control={control}
                />
              )}
            {!isSystemField && chosenType === ApiFieldValueTypes.entity_reference && (
              <Controller
                render={({ field: { onChange, value } }) => (
                  <Select
                    label={t("custom_fields:reference_directory")}
                    emptyTitle={t("common:select")}
                    value={value}
                    options={referenceDirectories}
                    onChange={(value) => {
                      onChange(value);
                      setValue("is_linked", false, { shouldDirty: true });
                    }}
                    required
                    disabled={isEdit}
                  />
                )}
                name={"reference_entity"}
                control={control}
              />
            )}
            {canLink(referenceEntity) && (
              <Controller
                render={({ field: { onChange, value } }) => (
                  <Switch
                    onChange={(checked) => {
                      onChange(checked);
                      if (checked) setValue("is_multiple", false, { shouldDirty: true });
                    }}
                    checked={value}
                    disabled={isEdit}
                    title={t(LINK_LABEL_KEYS[referenceEntity].title)}
                    description={t(LINK_LABEL_KEYS[referenceEntity].hint)}
                  />
                )}
                name="is_linked"
                control={control}
              />
            )}
            {!isSystemField && isTypeWithOptions && (
              <CustomFieldOptions
                highlightWarning={!!optionsError}
                options={currentOptions}
                updateOptions={setCurrentOptions}
              />
            )}
            {!isEdit && (
              <InformBox
                emoji={":bulb:"}
                title={t("custom_fields:just_reminder")}
                description={t("custom_fields:information_about_permissions", {
                  this_entity: t("custom_fields:this_field"),
                  entity_be_visible: t("custom_fields:field_be_visible"),
                })}
                actionsSlot={
                  isEdit && canIEdit(ApiPermission.settings_roles_permissions) ? (
                    <Stack items={"start"}>
                      <ButtonLink
                        href="/settings/roles-and-permissions"
                        target={"_blank"}
                        size={"sm"}
                        variant={"secondary"}
                        rightIcon={
                          <FontAwesomeIcon icon="fa-light fa-arrow-up-right-from-square" />
                        }
                      >
                        {t("custom_fields:open_role_permissions")}
                      </ButtonLink>
                    </Stack>
                  ) : (
                    <></>
                  )
                }
              />
            )}
            {field && !isSystemField ? (
              <DeleteListItem
                onDelete={handleDelete}
                title={t("custom_fields:confirmation_delete_field")}
              />
            ) : (
              <></>
            )}
          </Stack>
        </form>
      ) : (
        <></>
      )}
    </Modal>
  );
}
